import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  getCompany,
  createCompany,
  updateCompany,
} from "../../actions/companyActions";
import { getCollectiveLabourAgreements } from "../../actions/claActions";
import Grid from "@material-ui/core/Grid";
import Form from "./Form";
import {
  FormMode,
  InputElementSize,
  InputElementType,
} from "../../types/enums";
import InputElement from "../InputElement";

const CompanyForm = (props) => {
  const [name, setName] = useState("");
  const [contactPerson, setContactPerson] = useState("");
  const [email, setEmail] = useState("");
  const [invoiceEmail, setInvoiceEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [KVKNumber, setKVKNumber] = useState("");
  const [BTWNumber, setBTWNumber] = useState("");
  const [NDNumber, setNDNumber] = useState("");
  const [street, setStreet] = useState("");
  const [streetNumber, setStreetNumber] = useState("");
  const [zipcode, setZipcode] = useState("");
  const [city, setCity] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [gAccountNumber, setGAccountNumber] = useState("");
  const [isSecurityCompany, setIsSecurityCompany] = useState(false);
  const [cla, setCla] = useState("");
  const [optionClas, setOptionClas] = useState([]);

  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const isCreateMode = props.mode === FormMode.CREATE;
  const isUpdateMode = props.mode === FormMode.UPDATE;
  const isViewMode = props.mode === FormMode.VIEW;

  const actionName = isCreateMode
    ? "toevoegen"
    : isUpdateMode
      ? "bijwerken"
      : "";

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const clas = await getCollectiveLabourAgreements();
      setOptionClas(clas);

      if (isUpdateMode || isViewMode) {
        const company = await getCompany(props.companyEmail);

        const cla =
          (isViewMode
            ? (clas.find((o) => o.id === company.claId) || {}).name
            : company.claId) || "";

        setName(company.name);
        setContactPerson(company.contactPerson);
        setEmail(company.email);
        setInvoiceEmail(company.invoiceEmail);
        setPhone(company.phone);
        setKVKNumber(company.KVKNumber);
        setBTWNumber(company.BTWNumber);
        setNDNumber(company.NDNumber);
        setStreet(company.street);
        setStreetNumber(company.streetNumber);
        setZipcode(company.zipcode);
        setCity(company.city);
        setAccountNumber(company.accountNumber);
        setGAccountNumber(company.gAccountNumber);
        setIsSecurityCompany(company.isSecurityCompany);
        setCla(cla);
      }
    } catch (error) {
      console.error(error);
      setErrorMessage(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);

    const form = {
      name,
      contactPerson,
      email,
      invoiceEmail,
      phone,
      KVKNumber,
      BTWNumber,
      NDNumber,
      street,
      streetNumber,
      zipcode,
      city,
      accountNumber,
      gAccountNumber,
      isSecurityCompany,
      claId: cla,
    };

    const promise = isCreateMode
      ? createCompany(form)
      : isUpdateMode
        ? updateCompany(props.companyEmail, form)
        : null;

    promise
      .then(() => {
        if (isCreateMode) window.location.href = `/company`;
        setSuccessMessage(`Bedrijf ${actionName} is voltooid.`);
      })
      .catch(setErrorMessage)
      .finally(() => setIsLoading(false));
  };

  const inputs = [
    {
      id: "name",
      type: InputElementType.TEXT,
      label: "Bedrijfsnaam *",
      state: name,
      ...(!isViewMode && {
        setState: setName,
      }),
    },
    {
      id: "contactPerson",
      type: InputElementType.TEXT,
      label: "Contactpersoon *",
      state: contactPerson,
      ...(!isViewMode && {
        setState: setContactPerson,
      }),
    },
    {
      id: "isSecurityCompany",
      type: InputElementType.CHECKBOX,
      label: "Beveiligingsorganisatie",
      state: isSecurityCompany,
      size: InputElementSize.S,
      setState: setIsSecurityCompany,
    },
    {
      id: "claId",
      label: "CAO",
      type: isViewMode ? InputElementType.TEXT : InputElementType.SELECT,
      state: cla,
      size: InputElementSize.S,
      ...(!isViewMode && {
        setState: setCla,
        options: optionClas.map((o) => ({ id: o.id, label: o.name })),
      }),
    },
    {
      id: "email",
      type: InputElementType.TEXT,
      label: "Email *",
      state: email,
      ...(!isViewMode && {
        setState: setEmail,
      }),
    },
    {
      id: "invoiceEmail",
      type: InputElementType.TEXT,
      label: "Email voor facturen *",
      state: invoiceEmail,
      ...(!isViewMode && {
        setState: setInvoiceEmail,
      }),
    },
    {
      id: "phone",
      type: InputElementType.TEXT,
      label: "Telefoonnummer *",
      state: phone,
      ...(!isViewMode && {
        setState: setPhone,
      }),
    },
    {
      id: "KVKNumber",
      type: InputElementType.TEXT,
      label: "KVK-nummer *",
      state: KVKNumber,
      ...(!isViewMode && {
        setState: setKVKNumber,
      }),
    },
    {
      id: "BTWNumber",
      type: InputElementType.TEXT,
      label: "BTW-nummer *",
      state: BTWNumber,
      ...(!isViewMode && {
        setState: setBTWNumber,
      }),
    },
    {
      id: "NDNumber",
      type: InputElementType.TEXT,
      label: "ND-nummer *",
      state: NDNumber,
      ...(!isViewMode && {
        setState: setNDNumber,
      }),
    },
    {
      id: "street",
      type: InputElementType.TEXT,
      size: InputElementSize.S,
      label: "Adres *",
      state: street,
      ...(!isViewMode && {
        setState: setStreet,
      }),
    },
    {
      id: "streetNumber",
      type: InputElementType.TEXT,
      size: InputElementSize.S,
      label: "Huisnr. *",
      state: streetNumber,
      ...(!isViewMode && {
        setState: setStreetNumber,
      }),
    },
    {
      id: "zipcode",
      type: InputElementType.TEXT,
      label: "Postcode *",
      state: zipcode,
      ...(!isViewMode && {
        setState: setZipcode,
      }),
    },
    {
      id: "city",
      type: InputElementType.TEXT,
      label: "Plaats *",
      state: city,
      ...(!isViewMode && {
        setState: setCity,
      }),
    },
    {
      id: "accountNumber",
      type: InputElementType.TEXT,
      label: "Rekeningnummer *",
      state: accountNumber,
      ...(!isViewMode && {
        setState: setAccountNumber,
      }),
    },
    {
      id: "gAccountNumber",
      type: InputElementType.TEXT,
      label: "G-rekeningnummer *",
      state: gAccountNumber,
      ...(!isViewMode && {
        setState: setGAccountNumber,
      }),
    },
  ];

  return (
    <Form
      onSubmit={!isViewMode && handleSubmit}
      submitText={actionName}
      isFetching={isLoading}
      successMessage={successMessage}
      errorMessage={errorMessage}
      mode={props.mode}
    >
      <Grid container spacing={3}>
        {inputs.map((i) => (
          <InputElement key={i.id} item={i} />
        ))}
      </Grid>
    </Form>
  );
};

CompanyForm.propTypes = {
  companyEmail: PropTypes.string,
  mode: PropTypes.oneOf(Object.values(FormMode)).isRequired,
};

export default CompanyForm;



// WEBPACK FOOTER //
// ./src/components/forms/CompanyForm.js